import {
  buildCodeItem,
  buildTextItem,
} from "@/components/storyboard/quiz/review/buildReviewItem";
import {
  isResolved,
  type ValidationSubmission,
} from "@/lib/validationSubmission";
import {
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";

interface HandedInArgs {
  /** One task heading per entry, in entry order — the recap's question text. */
  prompts: string[];
  submissions: Record<number, ValidationSubmission>;
  /**
   * How the learner's answer is recapped: "code" highlights it as a snippet
   * (CODE_ASSERTION, CODE_DEBUG, CODE_FREETEXT), "text" echoes prose
   * (CODE_EXPLANATION).
   */
  answerKind?: "code" | "text";
  /** Highlighting for the recapped code, derived from the storyboard. */
  language?: string;
}

/**
 * Completed status for an attempt-capped code quiz, or undefined while any
 * task can still be worked on.
 *
 * The quiz is over once every task is right or out of attempts, which is read
 * off the saved submissions rather than local state — so the recap survives a
 * cleared browser storage and greets the learner on any other device too.
 */
export const codeQuizHandedInStatus = ({
  prompts,
  submissions,
  answerKind = "code",
  language,
}: HandedInArgs): InteractiveElementStatus | undefined => {
  const settled = prompts.map((_, index) => submissions[index]);
  if (
    !settled.every((submission) => submission != null && isResolved(submission))
  ) {
    return undefined;
  }

  return {
    kind: InteractiveElementStatusKind.Completed,
    score: {
      correct: settled.filter((submission) => submission.correct === true)
        .length,
      total: prompts.length,
    },
    review: {
      items: prompts.map((prompt, index) => {
        const answer = {
          prompt,
          userAnswer: settled[index].userInput,
          isCorrect: settled[index].correct === true,
          feedback: settled[index].feedback,
        };
        return answerKind === "text"
          ? buildTextItem(answer)
          : buildCodeItem({ ...answer, language });
      }),
    },
    attempts: 1,
    completedAt: new Date().toISOString(),
  };
};
